import { useCallback, useEffect, useRef, useState } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import {
  Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered,
  Link2, Link2Off, ImagePlus, Minus, Undo2, Redo2, Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  apiDrawUploadRuleImage, ApiRequestError, ruleImageSrc,
  withRuleImageOrigin, stripRuleImageOrigin,
} from '@/lib/api'

/**
 * The กติกา body for one lottery.
 *
 * Only the marks the API's allowlist keeps are offered. An editor that can produce
 * formatting the sanitiser strips teaches the operator to write something, save it, and
 * watch it vanish — so the toolbar and the allowlist are deliberately the same list.
 *
 * Images are uploaded the moment they are picked, not on save: the editor stores a URL,
 * and there is nowhere to keep megabytes of pending bytes while the operator keeps
 * typing. Anything uploaded and then removed before saving is swept by the API when the
 * body is stored.
 */

const MAX_IMAGE_BYTES = 2 * 1024 * 1024

function ToolbarButton({ active, disabled, title, onClick, children }: {
  active?: boolean
  disabled?: boolean
  title: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] border transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-40',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

function Divider() {
  return <span className="mx-0.5 h-5 w-px shrink-0 bg-border" aria-hidden />
}

function Toolbar({ editor, onPickImage, uploading }: {
  editor: Editor
  onPickImage: () => void
  uploading: boolean
}) {
  const setLink = useCallback(() => {
    const previous = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('ลิงก์ (ต้องขึ้นต้นด้วย https://)', previous ?? 'https://')
    if (url === null) return
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    // The API refuses anything but http(s); rejecting it here too means the operator
    // finds out while the cursor is still on the word, not after a save round-trip.
    if (!/^https?:\/\//i.test(url)) {
      window.alert('ลิงก์ต้องขึ้นต้นด้วย http:// หรือ https://')
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }, [editor])

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/30 px-1.5 py-1">
      <ToolbarButton title="ตัวหนา" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton title="ตัวเอียง" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton title="ขีดฆ่า" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="h-4 w-4" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton title="หัวข้อใหญ่" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <Heading2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton title="หัวข้อย่อย" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        <Heading3 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton title="รายการ" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton title="รายการเรียงเลข" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton title="เส้นคั่น" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <Minus className="h-4 w-4" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton title="ใส่ลิงก์" active={editor.isActive('link')} onClick={setLink}>
        <Link2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton title="เอาลิงก์ออก" disabled={!editor.isActive('link')} onClick={() => editor.chain().focus().unsetLink().run()}>
        <Link2Off className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton title="แทรกรูป" disabled={uploading} onClick={onPickImage}>
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
      </ToolbarButton>

      <Divider />

      <ToolbarButton title="ย้อนกลับ" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
        <Undo2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton title="ทำซ้ำ" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
        <Redo2 className="h-4 w-4" />
      </ToolbarButton>
    </div>
  )
}

export interface RulesEditorProps {
  lotteryId: string
  /** Stored body, with image paths relative to the API root. */
  value: string
  onChange: (html: string) => void
  disabled?: boolean
}

export function RulesEditor({ lotteryId, value, onChange, disabled = false }: RulesEditorProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        // Not on the API's allowlist — offering them would let an operator write
        // formatting that silently disappears on save.
        codeBlock: false,
        blockquote: false,
      }),
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({ openOnClick: false, autolink: false, protocols: ['http', 'https'] }),
    ],
    // Absolute while editing so the images actually render in this app; converted back to
    // the stored relative form on the way out.
    content: withRuleImageOrigin(value),
    onUpdate: ({ editor: e }) => onChange(stripRuleImageOrigin(e.getHTML())),
    editorProps: {
      attributes: {
        class: 'prose-rules min-h-[320px] px-3 py-3 focus:outline-none',
      },
    },
  })

  // Keep the editor in step when the parent swaps lottery, without stomping on the
  // operator mid-sentence: setContent resets the cursor, so only do it when the incoming
  // body is genuinely different from what is on screen.
  useEffect(() => {
    if (!editor) return
    const current = stripRuleImageOrigin(editor.getHTML())
    // `false` = do not emit an update, so swapping lottery is not mistaken for an edit.
    if (current !== value) editor.commands.setContent(withRuleImageOrigin(value), false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotteryId])

  useEffect(() => {
    editor?.setEditable(!disabled)
  }, [editor, disabled])

  const onFile = useCallback(async (file: File) => {
    setUploadError(null)
    if (file.size > MAX_IMAGE_BYTES) {
      setUploadError('ไฟล์รูปต้องไม่เกิน 2 MB')
      return
    }
    setUploading(true)
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result))
        reader.onerror = () => reject(new Error('อ่านไฟล์ไม่สำเร็จ'))
        reader.readAsDataURL(file)
      })
      const image = await apiDrawUploadRuleImage(lotteryId, base64)
      // Insert the absolute form so it renders here; onUpdate strips it back down.
      editor?.chain().focus().setImage({ src: ruleImageSrc(image.url) }).run()
    } catch (err) {
      setUploadError(err instanceof ApiRequestError ? err.message : 'อัปโหลดรูปไม่สำเร็จ')
    } finally {
      setUploading(false)
    }
  }, [editor, lotteryId])

  if (!editor) {
    return <div className="h-[380px] animate-pulse rounded-[var(--radius)] border border-border bg-muted/40" />
  }

  return (
    <div className="flex flex-col">
      <div className={cn(
        'overflow-hidden rounded-[var(--radius)] border border-border bg-card',
        disabled && 'opacity-60',
      )}>
        <Toolbar editor={editor} uploading={uploading} onPickImage={() => fileRef.current?.click()} />
        <EditorContent editor={editor} />
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          // Reset first so picking the same file twice still fires a change event.
          e.target.value = ''
          if (file) void onFile(file)
        }}
      />

      {uploadError && (
        <p role="alert" className="mt-1.5 text-[0.75rem] font-medium text-destructive">{uploadError}</p>
      )}
      <p className="mt-1.5 text-[0.6875rem] text-muted-foreground">
        รูปไม่เกิน 2 MB (PNG, JPEG, WEBP, GIF) · รูปที่เอาออกแล้วจะถูกลบให้อัตโนมัติตอนบันทึก
      </p>
    </div>
  )
}

/** Read-only render of a stored body — the same markup the member app will show. */
export function RulesPreview({ html }: { html: string }) {
  if (html.trim() === '') {
    return (
      <p className="px-3 py-8 text-center text-[0.8125rem] text-muted-foreground">
        ยังไม่ได้เขียนกติกาของหวยนี้
      </p>
    )
  }
  return (
    <div
      className="prose-rules px-3 py-3"
      // Sanitised by the API before it was stored — see rules.sanitize.ts. Rendering it
      // is the point of the field.
      dangerouslySetInnerHTML={{ __html: withRuleImageOrigin(html) }}
    />
  )
}
